/**
 * useBusinessTaxContext - Phase A / #5
 * =====================================
 *
 * Returns the tax columns the bank-feed table should render, derived from
 * the active business's country and tax_preferences. The hook caches the
 * result for 5 minutes and re-fetches when the user updates business
 * settings (via the `tax-context-changed` window event).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const CACHE_TTL_MS = 5 * 60 * 1000;
let cache = null;
let cacheAt = 0;
let cacheBizId = null;

const activeBizId = () => {
  try { return JSON.parse(localStorage.getItem('akountfly_business') || 'null')?.id || null; } catch { return null; }
};

export function useBusinessTaxContext() {
  const fresh = cache && cacheBizId === activeBizId();
  const [ctx, setCtx] = useState(fresh ? cache : null);
  const [loading, setLoading] = useState(!fresh);

  const fetch = useCallback(async () => {
    // Slow networks must not hide the tax columns: retry with a longer timeout before giving up.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const res = await axios.get(`${API}/bank-feeds/tax-context`, { timeout: 8000 + attempt * 8000 });
        cache = res.data;
        cacheAt = Date.now();
        cacheBizId = activeBizId();
        setCtx(res.data);
        setLoading(false);
        return;
      } catch (_e) {
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    setCtx({ country: null, supported: false, show_vat: false, show_wht: false, show_sales_tax: false, configure_url: '/settings' });
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!cache || cacheBizId !== activeBizId() || (Date.now() - cacheAt) > CACHE_TTL_MS) fetch();
    const handler = () => fetch();
    window.addEventListener('tax-context-changed', handler);
    return () => window.removeEventListener('tax-context-changed', handler);
  }, [fetch]);

  return { ctx, loading, refresh: fetch };
}

export default useBusinessTaxContext;
